// src/context/LeadContext.jsx
// Optimized: all team data lives here so pages never create duplicate
// Firestore listeners.  Subscriptions are deferred until uid is ready
// to prevent the double-subscribe flicker caused by undefined → uid.

import React, { createContext, useContext, useMemo, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useLeave } from "./LeaveContext";
import {
  useTeamMembers,
  useTeamTasks,
  useTeamLeaves,
  useTeamLeadAnnouncements,
  useTodayMeetings,
  useTeamTodayAttendance,
  getTodayStr,
} from "../hooks/useFirestore";
import { isPresent } from "../utils/attendanceStatus";
import { useHolidays } from "../hooks/useFirestoreExtensions";
import { calculateDailyStatsForEmployees } from "../utils/statusCalculations";

const LeadContext = createContext(null);

export function useLeadData() {
  const ctx = useContext(LeadContext);
  if (!ctx) throw new Error("useLeadData must be used within a LeadProvider");
  return ctx;
}

export function LeadProvider({ children }) {
  const { userProfile } = useAuth();

  // Wait for profile to settle before deriving uid.
  // Using a local "ready" flag prevents hooks from firing with undefined
  // and then immediately re-firing with the real uid (double-subscribe).
  const uid = userProfile?.uid ?? null;

  // ── Members (realtime) ──────────────────────────────────────
  const membersData = useTeamMembers(uid);
  const members = membersData.data;
  const memberIds = useMemo(() => members.map((m) => m.uid), [members]);

  // ── Today's attendance — team-scoped ONLY ───────────────────
  // Replaces useAllTodayAttendance() which fetched the entire org.
  const attendanceData = useTeamTodayAttendance(memberIds);

  // ── Tasks — team-scoped (realtime) ──────────────────────────
  const tasksData = useTeamTasks(uid);

  // ── All team leaves (for dashboard stats) ───────────────────
  const allLeavesData = useTeamLeaves(uid);

  // ── Announcements (realtime) ─────────────────────────────────
  const announcementsData = useTeamLeadAnnouncements(uid);

  // ── Today's meetings (shared hook, no duplicate) ────────────
  const meetingsData = useTodayMeetings();
  const todayMeetings = meetingsData.data; // already filtered: today + not expired
  const todayStr = getTodayStr();

  const { data: holidays } = useHolidays();

  // ── Pending leave requests (pending_lead) from LeaveContext ──
  const {
    teamRequests,
    teamRequestsLoading,
    subscribeToTeamRequests,
    reviewLeaveRequest,
  } = useLeave();

  useEffect(() => {
    if (!uid) return;
    const unsub = subscribeToTeamRequests(uid);
    return unsub;
  }, [subscribeToTeamRequests, uid]);

  // ── Derived dashboard stats (memoised) ──────────────────────
  const stats = useMemo(() => {
    const todayHoliday = holidays.find((h) => h.date === todayStr);
    const isHolidayToday = !!todayHoliday;

    const dailyStats = calculateDailyStatsForEmployees({
      employees: members,
      attendanceRecords: attendanceData.data,
      leaveRequests: allLeavesData.data,
      isHolidayToday,
      dateStr: todayStr,
    });

    const pendingTasks = tasksData.data.filter((t) => t.status !== "COMPLETED").length;
    const completedTasks = tasksData.data.filter((t) => t.status === "COMPLETED").length;
    const pendingApprovals = teamRequests.length;

    return {
      presentDays: dailyStats.present,
      attendRate: dailyStats.rate,
      pendingTasks,
      completedTasks,
      teamOnLeave: dailyStats.leaves,
      pendingApprovals,
    };
  }, [members, attendanceData.data, tasksData.data, allLeavesData.data, todayStr, teamRequests.length, holidays]);

  const value = useMemo(
    () => ({
      // Members
      members,
      membersLoading: membersData.loading,

      // Attendance (today, team-only)
      allAttendance: attendanceData.data,
      attLoading: attendanceData.loading,

      // Tasks
      tasks: tasksData.data,
      tasksLoading: tasksData.loading,

      // All team leaves (history)
      allLeaves: allLeavesData.data,
      allLeavesLoading: allLeavesData.loading,

      // Pending leave requests (Lead action queue)
      leaves: teamRequests,
      leavesLoading: teamRequestsLoading,

      // Announcements
      announcements: announcementsData.data,
      announcementsLoading: announcementsData.loading,

      // Meetings
      meetings: meetingsData.data,
      todayMeetings,
      meetingsLoading: meetingsData.loading,

      // Leave review action
      reviewLeaveRequest,

      // Pre-computed dashboard stats (avoids repeated filter loops in each page)
      stats,
    }),
    [
      members,
      membersData.loading,
      attendanceData.data,
      attendanceData.loading,
      tasksData.data,
      tasksData.loading,
      allLeavesData.data,
      allLeavesData.loading,
      teamRequests,
      teamRequestsLoading,
      announcementsData.data,
      announcementsData.loading,
      meetingsData.data,
      todayMeetings,
      meetingsData.loading,
      reviewLeaveRequest,
      stats,
    ],
  );

  return <LeadContext.Provider value={value}>{children}</LeadContext.Provider>;
}
